import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { progressNotesService } from '../../../services/supabaseService';

const ProgressNotesPanel = ({ userId, isClient }) => {
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    loadNotes();
  }, [userId]);

  const loadNotes = async () => {
    setIsLoading(true);
    try {
      const { data } = await progressNotesService?.getProgressNotes(userId);
      setNotes(data || []);
    } catch (error) {
      console.error('Error loading notes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote?.title || !newNote?.content) return;

    const { data, error } = await progressNotesService?.addProgressNote({
      ...newNote,
      clientId: userId
    });

    if (!error && data) {
      setNotes(prev => [data, ...prev]);
      setNewNote({ title: '', content: '', tags: [] });
      setIsAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    const { error } = await progressNotesService?.deleteProgressNote(noteId);
    if (!error) {
      setNotes(prev => prev?.filter(n => n?.id !== noteId));
    }
  };

  const handleAddTag = () => {
    if (tagInput?.trim() && !newNote?.tags?.includes(tagInput?.trim())) {
      setNewNote(prev => ({
        ...prev,
        tags: [...prev?.tags, tagInput?.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setNewNote(prev => ({
      ...prev,
      tags: prev?.tags?.filter(tag => tag !== tagToRemove)
    }));
  };

  const filteredNotes = notes?.filter(note =>
    note?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    note?.content?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    note?.tags?.some(tag => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Search and Add Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search notes by title, content, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            icon="Search"
          />
        </div>
        {isClient && (
          <Button onClick={() => setIsAddingNote(true)}>
            <Icon name="Plus" size={18} className="mr-2" />
            Add Note
          </Button>
        )}
      </div>

      {/* Add Note Form */}
      {isAddingNote && (
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-foreground">New Progress Note</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsAddingNote(false);
                setNewNote({ title: '', content: '', tags: [] });
              }}
            >
              <Icon name="X" size={18} />
            </Button>
          </div>

          <div className="space-y-4">
            <Input
              label="Title"
              placeholder="Note title"
              value={newNote?.title}
              onChange={(e) => setNewNote(prev => ({ ...prev, title: e?.target?.value }))}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Content</label>
              <textarea
                className="w-full min-h-[120px] px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Describe your progress, challenges, achievements..."
                value={newNote?.content}
                onChange={(e) => setNewNote(prev => ({ ...prev, content: e?.target?.value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Tags</label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add tag (e.g., milestone, challenge)"
                  value={tagInput}
                  onChange={(e) => setTagInput(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && handleAddTag()}
                />
                <Button onClick={handleAddTag} variant="outline">
                  Add
                </Button>
              </div>
              {newNote?.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {newNote?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                    >
                      {tag}
                      <button onClick={() => handleRemoveTag(tag)}>
                        <Icon name="X" size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button onClick={handleAddNote} fullWidth>
                Save Note
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddingNote(false);
                  setNewNote({ title: '', content: '', tags: [] });
                }}
                fullWidth
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Notes List */}
      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredNotes?.length > 0 ? (
            filteredNotes?.map((note) => (
              <div key={note?.id} className="bg-card border border-border rounded-xl p-6">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-1">{note?.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(note?.createdAt), 'MMMM dd, yyyy • h:mm a')}
                    </p>
                  </div>
                  {isClient && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteNote(note?.id)}
                    >
                      <Icon name="Trash2" size={18} className="text-destructive" />
                    </Button>
                  )}
                </div>

                <p className="text-foreground mb-4 whitespace-pre-wrap">{note?.content}</p>

                {note?.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {note?.tags?.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-muted text-muted-foreground rounded-full text-sm"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-20">
              <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchQuery ? 'No notes match your search' : 'No progress notes yet'}
              </p>
              {isClient && !searchQuery && (
                <Button onClick={() => setIsAddingNote(true)} className="mt-4">
                  Create Your First Note
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgressNotesPanel;